/* eslint-disable no-console */
import { ClaimContext } from 'context/claim/ClaimContext';
import { IClaimContext } from 'context/claim/claimTypes';
import { useContext, useEffect, useState } from 'react';
import { CPEAPIClient } from 'shared/api';
import { ActionType, DispatchFunction } from 'types/action';
import { IClaimState } from 'types/claim';

type UseCPEDataType = {
  claimState: IClaimState;
  claimDispatch: DispatchFunction;
};
export const useCPEData = (): UseCPEDataType => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { state: claimState, dispatch: claimDispatch } = useContext<
    IClaimContext
  >(ClaimContext);

  useEffect(() => {
    if (!claimState.claim?.id) {
      return;
    }
    const fetchCPEData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [
          policyResponse,
          inspectionResponse,
          incidentsResponse,
          auditTrailResponse,
        ] = await Promise.all([
          CPEAPIClient.get(`/v1/policies/${claimState.claim.policyId}`),
          CPEAPIClient.get(`/v1/claims/${claimState.claim.id}/inspections`),
          CPEAPIClient.get(`/v1/claims/${claimState.claim.id}/incidents`),
          CPEAPIClient.get(`/v1/claims/${claimState.claim.id}/auditTrail`),
        ]);

        const [
          policyData,
          inspectionData,
          incidentsData,
          auditTrailData,
        ] = await Promise.all([
          policyResponse.data,
          inspectionResponse.data[0],
          incidentsResponse.data[0],
          auditTrailResponse.data,
        ]);

        const vehiclesInvolvedResponse = await CPEAPIClient.get(
          `/v1/claims/${claimState.claim.id}/incidents/${incidentsData?.id}/vehiclesInvolved`,
        );

        const vehiclesInvolvedData = vehiclesInvolvedResponse.data;

        const personsInvolvedResponse = await Promise.all(
          vehiclesInvolvedData.map((vehicleInvolved) => {
            return CPEAPIClient.get(
              `/v1/persons?id=${vehicleInvolved.driverId}`,
            );
          }),
        );

        const personsInvolvedData = personsInvolvedResponse.map(
          (response) => response.data[0],
        );

        const claimFlagsResponse = await CPEAPIClient.get(
          `/v1/claims/${claimState.claim.id}/flags`,
        );
        const claimFlagsData = claimFlagsResponse.data;

        claimDispatch({
          type: ActionType.SET_CLAIM_POLICY,
          payload: policyData,
        });
        claimDispatch({
          type: ActionType.SET_CLAIM_INSPECTION,
          payload: inspectionData,
        });

        claimDispatch({
          type: ActionType.SET_CLAIM_INCIDENT,
          payload: incidentsData,
        });
        claimDispatch({
          type: ActionType.SET_CLAIM_PEOPLE_INVOLVED,
          payload: personsInvolvedData,
        });
        claimDispatch({
          type: ActionType.SET_CLAIM_VEHICLES_INVOLVED,
          payload: vehiclesInvolvedData,
        });

        claimDispatch({
          type: ActionType.SET_CLAIM_FLAGS,
          payload: claimFlagsData,
        });
        claimDispatch({
          type: ActionType.SET_CLAIM_AUDIT_TRAIL,
          payload: auditTrailResponse.data,
        });
      } catch (err) {
        setError(err as any);
      } finally {
        setLoading(false);
      }
    };
    fetchCPEData();
  }, [claimState.claim.id]);

  return { claimState, claimDispatch };
};
